/**
 * Recording Debug Diagnostic Overlay
 *
 * A 🩺 icon (top-left, mirroring the settings gear) that opens a scrollable
 * log panel showing every step of the adult recording flow in real-time.
 * Gated behind the 'recordingDebug' feature flag.
 *
 * Usage:
 *   import { recDbg } from '../utils/recordingDebug.js';
 *   recDbg.log('step-name', { any: 'detail' });
 *   recDbg.ok('Recording started on page 3');
 *   recDbg.warn('Last-page check skipped — reason');
 *   recDbg.error('sendRecordingAndFinish threw', err);
 */

let panelEl = null;
let logListEl = null;
let iconEl = null;
let visible = false;
let entries = [];
const MAX_ENTRIES = 200;

/**
 * Initialize the debug overlay icon (called once from NavigationBar or main).
 * Creates the 🩺 button in the top-left corner if not already present.
 * The icon is only visible when the story reader is active.
 */
export function initRecordingDebug() {
  if (iconEl) return; // already initialized

  // Create the icon button
  iconEl = document.createElement('button');
  iconEl.className = 'recording-debug-icon';
  iconEl.setAttribute('aria-label', 'Recording Diagnostics');
  iconEl.textContent = '🩺';
  iconEl.style.display = 'none'; // hidden until story-reader is active
  iconEl.addEventListener('click', togglePanel);
  document.body.appendChild(iconEl);

  // Create the panel (hidden by default)
  panelEl = document.createElement('div');
  panelEl.className = 'recording-debug-panel';
  panelEl.style.display = 'none';
  panelEl.innerHTML = `
    <div class="recording-debug-panel__header">
      <span class="recording-debug-panel__title">🩺 Recording Trace</span>
      <button class="recording-debug-panel__copy" title="Copy to clipboard">📋</button>
      <button class="recording-debug-panel__clear" title="Clear">🗑️</button>
      <button class="recording-debug-panel__close" title="Close">✕</button>
    </div>
    <ul class="recording-debug-panel__log"></ul>
  `;
  document.body.appendChild(panelEl);

  logListEl = panelEl.querySelector('.recording-debug-panel__log');
  panelEl.querySelector('.recording-debug-panel__close').addEventListener('click', togglePanel);
  panelEl.querySelector('.recording-debug-panel__clear').addEventListener('click', clearLog);
  panelEl.querySelector('.recording-debug-panel__copy').addEventListener('click', copyToClipboard);

  // Watch for story-reader appearing/disappearing to show/hide the icon
  startVisibilityWatch();

  // Inject styles
  injectStyles();
}

function togglePanel() {
  visible = !visible;
  panelEl.style.display = visible ? 'flex' : 'none';
  if (visible) {
    logListEl.scrollTop = logListEl.scrollHeight;
  }
}

function clearLog() {
  entries = [];
  if (logListEl) logListEl.innerHTML = '';
}

/**
 * Copy the full trace log to clipboard as plain text.
 */
function copyToClipboard() {
  const text = entries
    .map((e) => {
      const detailStr = e.detail !== undefined
        ? ` — ${typeof e.detail === 'object' ? JSON.stringify(e.detail) : e.detail}`
        : '';
      return `[${e.time}] ${e.icon} ${e.message}${detailStr}`;
    })
    .join('\n');

  navigator.clipboard.writeText(text || '(empty trace)').then(() => {
    const btn = panelEl.querySelector('.recording-debug-panel__copy');
    if (btn) {
      btn.textContent = '✓';
      setTimeout(() => { btn.textContent = '📋'; }, 1500);
    }
  }).catch(() => {
    // Fallback: select-all in a temporary textarea
    const ta = document.createElement('textarea');
    ta.value = text || '(empty trace)';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    ta.remove();
  });
}

/**
 * MutationObserver that watches for .story-reader-container appearing/disappearing
 * in the DOM. The 🩺 icon is only visible on the story reader page.
 */
function startVisibilityWatch() {
  // If diagnosticAlwaysVisible flag is on, keep icon visible at all times
  import('../config/featureFlags.js').then(({ isFeatureEnabled }) => {
    if (isFeatureEnabled('diagnosticAlwaysVisible')) {
      if (iconEl) iconEl.style.display = 'flex';
      // No observer needed — always visible
      return;
    }

    // Default: only show on story-reader
    const update = () => {
      const onReader = !!document.querySelector('.story-reader-container');
      if (iconEl) iconEl.style.display = onReader ? 'flex' : 'none';
      if (!onReader && visible) {
        visible = false;
        if (panelEl) panelEl.style.display = 'none';
      }
    };

    update();
    const target = document.getElementById('app') || document.body;
    const observer = new MutationObserver(update);
    observer.observe(target, { childList: true, subtree: true });
  });
}

function addEntry(level, message, detail) {
  const now = new Date();
  const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}.${String(now.getMilliseconds()).padStart(3, '0')}`;

  const icons = { ok: '✅', warn: '⚠️', error: '❌', info: 'ℹ️' };
  const icon = icons[level] || 'ℹ️';

  const entry = { time, level, icon, message, detail };
  entries.push(entry);
  if (entries.length > MAX_ENTRIES) entries.shift();

  if (logListEl) {
    const li = document.createElement('li');
    li.className = `recording-debug-entry recording-debug-entry--${level}`;
    const detailStr = detail !== undefined ? ` — ${typeof detail === 'object' ? JSON.stringify(detail) : detail}` : '';
    li.innerHTML = `<span class="recording-debug-entry__time">${time}</span> ${icon} ${escapeHtml(message)}${detailStr ? `<span class="recording-debug-entry__detail">${escapeHtml(String(detailStr))}</span>` : ''}`;
    logListEl.appendChild(li);
    logListEl.scrollTop = logListEl.scrollHeight;
  }
}

function escapeHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Public API — call from anywhere in the recording flow.
 */
export const recDbg = {
  log: (msg, detail) => addEntry('info', msg, detail),
  ok: (msg, detail) => addEntry('ok', msg, detail),
  warn: (msg, detail) => addEntry('warn', msg, detail),
  error: (msg, detail) => addEntry('error', msg, detail),
  /** Convenience: trace a function call with its arguments */
  trace: (fnName, args) => addEntry('info', `→ ${fnName}()`, args),
  /** Loader state logging — only writes to panel if ?debug-loader=true */
  loader: (msg, detail) => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug-loader') === 'true') {
      addEntry('info', `[LOADER] ${msg}`, detail);
    }
  },
};

// Also expose on window for console access
if (typeof window !== 'undefined') {
  window.recDbg = recDbg;
}

function injectStyles() {
  if (document.getElementById('recording-debug-styles')) return;
  const style = document.createElement('style');
  style.id = 'recording-debug-styles';
  style.textContent = `
    .recording-debug-icon {
      position: fixed;
      top: 8px;
      left: 8px;
      width: 34px;
      height: 34px;
      z-index: 9999;
      background: none;
      border: none;
      font-size: 22px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: background 0.2s ease;
    }
    .recording-debug-icon:hover {
      background: rgba(0,0,0,0.1);
    }

    [data-category="phone"] .recording-debug-icon,
    [data-category="foldable"] .recording-debug-icon {
      top: 5px;
      left: 5px;
      width: 28px;
      height: 28px;
      font-size: 18px;
    }

    .recording-debug-panel {
      position: fixed;
      top: 50px;
      left: 8px;
      right: 8px;
      bottom: 60px;
      z-index: 10000;
      background: rgba(0, 0, 0, 0.92);
      color: #e0e0e0;
      border-radius: 12px;
      display: flex;
      flex-direction: column;
      font-family: 'Courier New', monospace;
      font-size: 11px;
      overflow: hidden;
      box-shadow: 0 8px 32px rgba(0,0,0,0.5);
    }

    .recording-debug-panel__header {
      display: flex;
      align-items: center;
      padding: 8px 12px;
      background: #1a1a1a;
      border-bottom: 1px solid #333;
      gap: 8px;
    }

    .recording-debug-panel__title {
      flex: 1;
      font-weight: 700;
      font-size: 13px;
      color: #4ECDC4;
    }

    .recording-debug-panel__copy,
    .recording-debug-panel__clear,
    .recording-debug-panel__close {
      background: none;
      border: none;
      color: #999;
      font-size: 16px;
      cursor: pointer;
      padding: 4px;
    }

    .recording-debug-panel__close:hover { color: #ff5555; }
    .recording-debug-panel__clear:hover { color: #ffaa00; }
    .recording-debug-panel__copy:hover { color: #4ECDC4; }

    .recording-debug-panel__log {
      flex: 1;
      overflow-y: auto;
      padding: 8px;
      margin: 0;
      list-style: none;
      -webkit-overflow-scrolling: touch;
    }

    .recording-debug-entry {
      padding: 3px 0;
      border-bottom: 1px solid #222;
      word-break: break-all;
      line-height: 1.4;
    }

    .recording-debug-entry__time {
      color: #888;
      margin-right: 6px;
    }

    .recording-debug-entry__detail {
      color: #999;
      font-size: 10px;
    }

    .recording-debug-entry--ok { color: #4caf50; }
    .recording-debug-entry--warn { color: #ff9800; }
    .recording-debug-entry--error { color: #f44336; }
    .recording-debug-entry--info { color: #e0e0e0; }
  `;
  document.head.appendChild(style);
}
